<script setup>
import { ref, computed } from 'vue'
import { useInstantBookingSlotsStore } from '@/stores/instantBookingSlots.store'
import moment from 'moment-timezone'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  reservation: {
    type: Object,
    required: true
  },
  slot: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'cancellation-complete'])

const instantBookingSlotsStore = useInstantBookingSlotsStore()

// Dialog state
const confirmationStep = ref(false)
const actionType = ref(null) // 'cancel' or 'transfer'
const submitting = ref(false)
const errorMessage = ref('')

// Format currency
const formatCurrency = amount => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount)
}

// Calculate if within cancellation period
const isWithinCancellationPeriod = computed(() => {
  if (!props.slot) {
    return false
  }

  const tz = props.slot.restaurantTimeZone || 'America/New_York'
  const now = moment.tz(tz)

  // Use expirationTime if set, otherwise calculate from cancellationPeriodHours
  let deadline
  if (props.slot.expirationTime) {
    deadline = moment.tz(props.slot.expirationTime, tz)
  } else if (props.slot.date && props.slot.cancellationPeriodHours) {
    const reservationTime = moment.tz(props.slot.date, tz)
    deadline = moment(reservationTime).subtract(props.slot.cancellationPeriodHours, 'hours')
  } else {
    return false
  }

  return now.isBefore(deadline)
})

// Calculate penalty amount (per person)
const penaltyAmountPerPerson = computed(() => {
  if (!props.slot?.penalty) return 0

  const { penalty, guaranteedSpendPerPerson } = props.slot

  if (penalty.type === 'fixed') {
    return penalty.amount
  }

  if (penalty.type === 'percentage') {
    return guaranteedSpendPerPerson * (penalty.amount / 100)
  }

  return 0
})

// Calculate total penalty amount
const totalPenaltyAmount = computed(() => {
  if (!props.slot) return 0
  
  return penaltyAmountPerPerson.value * props.slot.partySize
})

// Calculate refund amount
const refundAmount = computed(() => {
  if (!props.slot) return 0

  if (isWithinCancellationPeriod.value) {
    // Within cancellation period: full refund
    return props.slot.totalAmount
  } else {
    // Past deadline: refund total minus penalty
    return Math.max(0, props.slot.totalAmount - totalPenaltyAmount.value)
  }
})

// Format cancellation deadline
const formattedDeadline = computed(() => {
  if (!props.slot) return ''
  const tz = props.slot.restaurantTimeZone || 'America/New_York'

  // Use expirationTime if set, otherwise calculate from cancellationPeriodHours
  let deadline
  if (props.slot.expirationTime) {
    deadline = moment.tz(props.slot.expirationTime, tz)
  } else if (props.slot.date && props.slot.cancellationPeriodHours) {
    const reservationTime = moment.tz(props.slot.date, tz)
    deadline = moment(reservationTime).subtract(props.slot.cancellationPeriodHours, 'hours')
  } else {
    return ''
  }

  return deadline.format('MMMM D, YYYY [at] h:mm A z')
})

// Check if non-refundable (refund would be zero after penalty)
const isNonRefundable = computed(() => {
  if (isWithinCancellationPeriod.value) return false
  
  return refundAmount.value <= 0
})

// Check if transfer is available
const canRequestTransfer = computed(() => {
  return isNonRefundable.value
})

// Get action button text
const actionButtonText = computed(() => {
  if (confirmationStep.value) {
    return submitting.value ? 'Processing...' : 'Confirm'
  }
  if (actionType.value === 'transfer') {
    return 'Request Transfer'
  }
  
  return 'Cancel Reservation'
})

// Get confirmation message
const confirmationMessage = computed(() => {
  if (actionType.value === 'transfer') {
    return 'Your slot will be made available to other members. You will only receive a refund if another member books your slot before the reservation time.'
  }

  if (isWithinCancellationPeriod.value) {
    
    return `Your reservation will be cancelled and your credit card hold will be released in full`
  }

  if (refundAmount.value > 0) {
    return `You are canceling past the deadline. A penalty of ${formatCurrency(totalPenaltyAmount.value)} will be applied. You will receive a partial refund of ${formatCurrency(refundAmount.value)}.`
  }

  return 'You are canceling past the deadline. This reservation is non-refundable per the cancellation policy.'
})

// Handle cancel button click
const handleCancel = () => {
  actionType.value = 'cancel'
  confirmationStep.value = true
}

// Handle transfer button click
const handleTransfer = () => {
  actionType.value = 'transfer'
  confirmationStep.value = true
}

// Handle back button
const handleBack = () => {
  confirmationStep.value = false
  actionType.value = null
  errorMessage.value = ''
}

// Handle confirmation
const handleConfirm = async () => {
  if (submitting.value) return

  submitting.value = true
  errorMessage.value = ''

  try {
    if (actionType.value === 'transfer') {
      // Request transfer
      await instantBookingSlotsStore.requestTransfer(props.slot._id)
      emit('cancellation-complete', {
        type: 'transfer',
        message: 'Transfer request submitted successfully'
      })
    } else {
      // Cancel booking
      const result = await instantBookingSlotsStore.cancelBooking(
        props.slot._id,
        props.reservation._id
      )
      emit('cancellation-complete', {
        type: 'cancel',
        refundAmount: result.refundAmount,
        slotReopened: result.slotReopened
      })
    }

    // Close dialog
    close()
  } catch (error) {
    console.error('Cancellation failed:', error)
    errorMessage.value = error.message || 'An error occurred. Please try again.'
  } finally {
    submitting.value = false
  }
}

// Close dialog
const close = () => {
  confirmationStep.value = false
  actionType.value = null
  errorMessage.value = ''
  emit('update:modelValue', false)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="600"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <VCard>
      <!-- Header -->
      <VCardTitle class="d-flex align-center gap-2 bg-grey-lighten-4">
        <VIcon
          v-if="!confirmationStep"
          icon="mdi-calendar-remove"
          size="24"
        />
        <VIcon
          v-else
          icon="mdi-alert-circle"
          size="24"
          color="warning"
        />
        <span>
          {{ confirmationStep ? 'Confirm Action' : 'Cancel Reservation' }}
        </span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="close"
        />
      </VCardTitle>

      <VCardText class="pa-6">
        <!-- Step 1: Policy Info & Action Selection -->
        <div v-if="!confirmationStep">
          <!-- Reservation Details -->
          <div class="mb-4">
            <h3 class="text-h6 font-weight-bold mb-2">
              Reservation Details
            </h3>
            <div class="details-grid">
              <div class="detail-row">
                <span class="label">Restaurant:</span>
                <span class="value">{{ slot.restaurantName }}</span>
              </div>
              <div class="detail-row">
                <span class="label">Date & Time:</span>
                <span class="value">{{ moment(slot.date).tz(slot.restaurantTimeZone).format('MMM D, YYYY [at] h:mm A') }}</span>
              </div>
              <div class="detail-row">
                <span class="label">Party Size:</span>
                <span class="value">{{ slot.partySize }} guests</span>
              </div>
              <div class="detail-row">
                <span class="label">Total Amount:</span>
                <span class="value font-weight-bold">{{ formatCurrency(slot.totalAmount) }}</span>
              </div>
            </div>
          </div>

          <VDivider class="my-4" />

          <!-- Cancellation Policy -->
          <div class="mb-4">
            <h3 class="text-h6 font-weight-bold mb-2">
              Cancellation Policy
            </h3>
            <VAlert
              :color="isWithinCancellationPeriod ? 'success' : 'warning'"
              variant="tonal"
              class="mb-3"
            >
              <div class="d-flex align-center gap-2">
                <VIcon
                  :icon="isWithinCancellationPeriod ? 'mdi-check-circle' : 'mdi-alert'"
                  size="20"
                />
                <div>
                  <p class="text-body-2 font-weight-medium mb-1">
                    {{ isWithinCancellationPeriod ? 'Within Cancellation Period' : 'Past Cancellation Deadline' }}
                  </p>
                  <p class="text-caption mb-0">
                    Deadline: {{ formattedDeadline }}
                  </p>
                </div>
              </div>
            </VAlert>

            <div class="policy-details">
              <p class="text-body-2 mb-2">
                <template v-if="isWithinCancellationPeriod">
                  Your credit card hold for the total of {{ formatCurrency(refundAmount) }} will be released in full.
                </template>
                <template v-else-if="refundAmount > 0">
                  Your credit card hold for the total of {{ formatCurrency(refundAmount) }} will be released in full.
                </template>
                <template v-else>
                  Non-refundable
                </template>
              </p>
            </div>
          </div>

          <!-- Error Message -->
          <VAlert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="mb-4"
            closable
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </VAlert>
        </div>

        <!-- Step 2: Confirmation -->
        <div v-else>
          <VAlert
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            <p class="text-body-2 mb-0">
              {{ confirmationMessage }}
            </p>
          </VAlert>

          <p class="text-body-2 text-medium-emphasis mb-0">
            This action cannot be undone. Are you sure you want to proceed?
          </p>

          <!-- Error Message -->
          <VAlert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            class="mt-4"
            closable
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </VAlert>
        </div>
      </VCardText>

      <!-- Actions -->
      <VCardActions class="pa-6 pt-0">
        <VSpacer />
        <template v-if="!confirmationStep">
          <!-- Regular Cancel Button -->
          <VBtn
            color="error"
            variant="flat"
            @click="handleCancel"
          >
            <VIcon
              icon="mdi-cancel"
              class="mr-1"
            />
            Cancel Reservation
          </VBtn>

          <!-- Transfer Button (if non-refundable) -->
          <VBtn
            v-if="canRequestTransfer"
            color="primary"
            variant="outlined"
            @click="handleTransfer"
          >
            <VIcon
              icon="mdi-swap-horizontal"
              class="mr-1"
            />
            Request Transfer
          </VBtn>

          <!-- Close Button -->
          <VBtn
            variant="text"
            @click="close"
          >
            Close
          </VBtn>
        </template>

        <template v-else>
          <!-- Back Button -->
          <VBtn
            variant="text"
            :disabled="submitting"
            @click="handleBack"
          >
            <VIcon
              icon="mdi-arrow-left"
              class="mr-1"
            />
            Back
          </VBtn>

          <!-- Confirm Button -->
          <VBtn
            :color="actionType === 'transfer' ? 'primary' : 'error'"
            variant="flat"
            :loading="submitting"
            :disabled="submitting"
            @click="handleConfirm"
          >
            {{ actionButtonText }}
          </VBtn>
        </template>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.details-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.08);

  &:last-child {
    border-bottom: none;
  }

  .label {
    color: rgb(var(--v-theme-on-surface-variant));
    font-size: 0.875rem;
    color: var(--ds-text-strong);
  }

  .value {
    font-size: 0.875rem;
    text-align: right;
  }
}

.policy-details {
  padding: 12px;
  background: rgba(var(--v-theme-surface-variant), 0.1);
  border-radius: 4px;
}
</style>
